/**
 * Branded Address Types
 *
 * This module provides type-safe address types for EVM and Solana chains.
 * Addresses are branded string types that carry proof of validation through
 * the type system.
 *
 * ## Design Philosophy
 *
 * 1. **Branded Types**: Addresses are strings with phantom brands that
 *    distinguish them at compile time while having zero runtime overhead.
 *
 * 2. **Validation at Boundaries**: Raw strings are validated when entering
 *    the system (API inputs, user input, external data). Once validated,
 *    the branded type carries the proof.
 *
 * 3. **Type Guards for Narrowing**: Type guards allow runtime discrimination
 *    while informing the type system.
 *
 * ## Chain-Specific Characteristics
 *
 * ### EVM Addresses
 * - Format: `0x` prefix + 40 hexadecimal characters
 * - Case-insensitive (normalized to lowercase)
 * - Example: `0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2`
 *
 * ### Solana Addresses
 * - Format: 32-44 Base58 characters
 * - Case-SENSITIVE (Base58 encoding)
 * - Example: `EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v`
 *
 * @module
 */

import { z } from "zod";
import type { Brand } from "./branded";
import type {
  Base58Char,
  IsValidEvmAddressFormat,
  IsValidSolanaAddressFormat,
} from "./string-type-utils";

// =============================================================================
// Brand Symbols
// =============================================================================

/**
 * Unique symbol for branding EVM addresses.
 * @internal
 */
declare const __evmAddressBrand: unique symbol;

/**
 * Unique symbol for branding Solana addresses.
 * @internal
 */
declare const __solanaAddressBrand: unique symbol;

// =============================================================================
// Branded Address Types
// =============================================================================

/**
 * A validated EVM (Ethereum Virtual Machine) address.
 *
 * This is a branded string type that represents an EVM address that has been
 * validated to be in the correct format: `0x` followed by 40 hexadecimal characters.
 *
 * The address is normalized to lowercase for consistent comparison and storage.
 *
 * @example
 * ```typescript
 * // Creating from a validated string
 * const addr = parseEvmAddress("0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2");
 *
 * // Type guards for narrowing
 * function process(addr: Address) {
 *   if (isEvmAddress(addr)) {
 *     // addr is narrowed to EvmAddress
 *   }
 * }
 * ```
 */
export type EvmAddress = Brand<`0x${string}`, typeof __evmAddressBrand>;

/**
 * A validated Solana address.
 *
 * This is a branded string type that represents a Solana address that has been
 * validated to be in the correct format: 32-44 Base58 characters.
 *
 * IMPORTANT: Solana addresses are case-sensitive. Never lowercase them.
 *
 * @example
 * ```typescript
 * // Creating from a validated string
 * const addr = parseSolanaAddress("EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v");
 *
 * // Type guards for narrowing
 * function process(addr: Address) {
 *   if (isSolanaAddress(addr)) {
 *     // addr is narrowed to SolanaAddress
 *   }
 * }
 * ```
 */
export type SolanaAddress = Brand<string, typeof __solanaAddressBrand>;

/**
 * Union of all supported address types.
 *
 * Use type guards `isEvmAddress` and `isSolanaAddress` to narrow.
 */
export type Address = EvmAddress | SolanaAddress;

// WalletAddress can be either EVM or Solana (SVM).
export type WalletAddress = Address;

// =============================================================================
// Type-Level Validation (for literals)
// =============================================================================

/**
 * Compile-time validated EVM address type.
 *
 * When used with a string literal, TypeScript will validate the format at compile time.
 * Returns `never` if the format is invalid.
 *
 * @template S - The string literal to validate
 *
 * @example
 * ```typescript
 * // Valid - compiles
 * type Valid = ValidatedEvmAddress<"0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2">;
 *
 * // Invalid - becomes `never`
 * type Invalid = ValidatedEvmAddress<"not-an-address">;
 * ```
 */
export type ValidatedEvmAddress<S extends string> =
  IsValidEvmAddressFormat<S> extends true ? S & EvmAddress : never;

/**
 * Compile-time validated Solana address type.
 *
 * When used with a string literal, TypeScript will validate the format at compile time.
 * Returns `never` if the format is invalid.
 *
 * @template S - The string literal to validate
 */
export type ValidatedSolanaAddress<S extends string> =
  IsValidSolanaAddressFormat<S> extends true ? S & SolanaAddress : never;

/**
 * Compile-time validated address (EVM or Solana).
 *
 * Returns the appropriately branded type based on format validation.
 *
 * @template S - The string literal to validate
 */
export type ValidatedAddress<S extends string> =
  IsValidEvmAddressFormat<S> extends true
    ? S & EvmAddress
    : IsValidSolanaAddressFormat<S> extends true
      ? S & SolanaAddress
      : never;

// =============================================================================
// Template Literal Prefix Types (for partial compile-time safety)
// =============================================================================

/**
 * Template literal type that ensures a string starts with valid Base58 characters.
 *
 * This catches obvious mistakes (like using an EVM address where Solana expected)
 * at compile time, without the full validation overhead.
 *
 * @example
 * ```typescript
 * // Catches EVM addresses used as Solana
 * const bad: SolanaAddressLike = "0xabc...";  // Error! '0' is not Base58Char
 * ```
 */
export type SolanaAddressLike = `${Base58Char}${Base58Char}${string}`;

/**
 * Template literal type that ensures a string starts with 0x prefix.
 */
export type EvmAddressLike = `0x${string}`;

// =============================================================================
// Chain Type Discriminant
// =============================================================================

/**
 * Discriminant type for address chain.
 */
export type AddressChainType = "evm" | "solana";

/**
 * Gets the chain type for an address type.
 */
export type ChainTypeOf<T extends Address> = T extends EvmAddress
  ? "evm"
  : T extends SolanaAddress
    ? "solana"
    : never;

// =============================================================================
// Regex Patterns (for runtime validation)
// =============================================================================

/**
 * Regex pattern for EVM addresses.
 * Matches: 0x followed by exactly 40 hex characters (case-insensitive).
 */
export const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;

/**
 * Regex pattern for Solana addresses.
 * Matches: 32-44 Base58 characters (no 0, O, I, l).
 */
export const SOLANA_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/;

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Type guard to check if a string is a valid EVM address format.
 *
 * This performs runtime validation and narrows the type to EvmAddress.
 *
 * @param value - The string to check
 * @returns True if the string is a valid EVM address format
 *
 * @example
 * ```typescript
 * function process(addr: string) {
 *   if (isEvmAddressFormat(addr)) {
 *     // addr is now typed as EvmAddress
 *     sendToEvm(addr);
 *   }
 * }
 * ```
 */
export function isEvmAddressFormat(value: string): value is EvmAddress {
  return EVM_ADDRESS_REGEX.test(value);
}

/**
 * Type guard to check if a string is a valid Solana address format.
 *
 * This performs runtime validation and narrows the type to SolanaAddress.
 *
 * @param value - The string to check
 * @returns True if the string is a valid Solana address format
 */
export function isSolanaAddressFormat(value: string): value is SolanaAddress {
  return SOLANA_ADDRESS_REGEX.test(value);
}

/**
 * Type guard to check if an Address is an EvmAddress.
 *
 * @param addr - The address to check
 * @returns True if the address is an EVM address
 */
export function isEvmAddress(addr: Address): addr is EvmAddress {
  return (addr as string).startsWith("0x");
}

/**
 * Type guard to check if an Address is a SolanaAddress.
 *
 * @param addr - The address to check
 * @returns True if the address is a Solana address
 */
export function isSolanaAddress(addr: Address): addr is SolanaAddress {
  return !(addr as string).startsWith("0x");
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Gets the chain type for an address.
 *
 * @param addr - The address to check
 * @returns The chain type ("evm" or "solana")
 */
export function getAddressChainType(addr: Address): AddressChainType {
  return isEvmAddress(addr) ? "evm" : "solana";
}

/**
 * Normalizes an address according to chain-specific rules.
 *
 * - EVM: lowercased (case-insensitive)
 * - Solana: preserved as-is (case-sensitive)
 *
 * @param addr - The address to normalize
 * @returns The normalized address
 */
export function normalizeAddress<T extends Address>(addr: T): T {
  if (isEvmAddress(addr)) {
    return (addr as string).toLowerCase() as T;
  }
  // Solana addresses are case-sensitive, don't modify
  return addr;
}

/**
 * Compares two addresses for equality.
 *
 * Uses chain-appropriate comparison:
 * - EVM: case-insensitive
 * - Solana: case-sensitive
 *
 * @param a - First address
 * @param b - Second address
 * @returns True if addresses are equal
 */
export function addressEquals(a: Address, b: Address): boolean {
  // Different chain types can't be equal
  if (isEvmAddress(a) !== isEvmAddress(b)) {
    return false;
  }

  if (isEvmAddress(a)) {
    return (a as string).toLowerCase() === (b as string).toLowerCase();
  }

  // Solana: case-sensitive comparison
  return a === b;
}

// =============================================================================
// Zod Schemas (for runtime validation in API routes)
// =============================================================================

/**
 * Zod schema for validating Solana addresses.
 *
 * Validates that the string matches the Base58 format (32-44 chars, no 0/O/I/l).
 *
 * @example
 * ```typescript
 * const input = z.object({
 *   walletAddress: SolanaAddressSchema,
 * });
 * ```
 */
export const SolanaAddressSchema = z
  .string()
  .regex(SOLANA_ADDRESS_REGEX, "Invalid Solana address");

/**
 * Zod schema for validating EVM addresses.
 *
 * Validates that the string matches the EVM format (0x + 40 hex chars).
 *
 * @example
 * ```typescript
 * const input = z.object({
 *   walletAddress: EvmAddressSchema,
 * });
 * ```
 */
export const EvmAddressSchema = z
  .string()
  .regex(EVM_ADDRESS_REGEX, "Invalid EVM address");

/**
 * Zod schema for validating either EVM or Solana addresses.
 *
 * Validates that the string is a valid address format for either chain.
 *
 * @example
 * ```typescript
 * const input = z.object({
 *   walletAddress: EthOrSolanaAddressSchema,
 * });
 * ```
 */
export const EthOrSolanaAddressSchema = z
  .string()
  .refine(
    (val) => isEvmAddressFormat(val) || isSolanaAddressFormat(val),
    "Invalid Ethereum or Solana address",
  );
